import { useEffect, useState } from "react";
import { Check, KeyRound, Loader2, Mail, Send, User } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { PasswordPolicyChecklist } from "../PasswordPolicyChecklist";
import { UserDirectories } from "../UserDirectories";
import { useAuth } from "../../context/AuthContext";
import { api, apiError } from "../../lib/api";
import i18n, { countryName } from "../../i18n";

const EmailChangeDialog = ({ open, onOpenChange, currentEmail, onDone }) => {
  const [step, setStep] = useState(1);
  const [newEmail, setNewEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const close = (value) => {
    if (!value) { setStep(1); setNewEmail(""); setPassword(""); setCode(""); }
    onOpenChange(value);
  };

  const requestChange = async () => {
    if (!newEmail.trim() || !password) return toast.error("Renseignez la nouvelle adresse et votre mot de passe actuel.");
    setBusy(true);
    try {
      await api.post("/account/email-change/request", { new_email: newEmail.trim(), current_password: password });
      setStep(2);
      toast.success("Un code de validation a été envoyé à la nouvelle adresse.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const verifyChange = async () => {
    if (!code.trim()) return toast.error("Saisissez le code reçu.");
    setBusy(true);
    try {
      const { data } = await api.post("/account/email-change/verify", { code: code.trim() });
      onDone(data.user, data.directory_proposal_candidates || 0);
      toast.success("Adresse e-mail mise à jour.");
      close(false);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Modifier l'adresse e-mail</DialogTitle>
          <DialogDescription>{step === 1 ? "Confirmez votre mot de passe puis renseignez la nouvelle adresse." : "Saisissez le code reçu sur la nouvelle adresse."}</DialogDescription>
        </DialogHeader>
        {step === 1 ? (
          <div className="space-y-4">
            <div><Label className="mb-1.5 block">Adresse actuelle</Label><Input value={currentEmail} readOnly disabled /></div>
            <div><Label className="mb-1.5 block">Nouvelle adresse e-mail</Label><Input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} /></div>
            <div><Label className="mb-1.5 block">Mot de passe actuel</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} /></div>
            <Button onClick={requestChange} disabled={busy} className="w-full bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />} Envoyer le code
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div><Label className="mb-1.5 block">Code de validation</Label><Input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" className="text-center tracking-[0.35em]" /></div>
            <Button onClick={verifyChange} disabled={busy} className="w-full bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />} Valider
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const ProfilePage = () => {
  const { user, setUser, activeWorkspace, activeWorkspaceId } = useAuth();
  const [profile, setProfile] = useState({ first_name: "", last_name: "", phone: "", update_mode: "ask" });
  const [saving, setSaving] = useState(false);
  const [proposalCandidates, setProposalCandidates] = useState(0);
  const [proposingAll, setProposingAll] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [changingPwd, setChangingPwd] = useState(false);
  const [pwdPolicy, setPwdPolicy] = useState(null);
  const [pwdExpiry, setPwdExpiry] = useState(null);

  useEffect(() => {
    if (!user) return;
    setProfile({
      first_name: user.first_name || "",
      last_name: user.last_name || "",
      phone: user.phone || "",
      update_mode: user.directory_profile_update_mode || "ask",
    });
    api.get("/account/password-policy").then(({ data }) => {
      setPwdPolicy(data.policy);
      setPwdExpiry(data.expiry);
    }).catch(() => {});
  }, [user, activeWorkspaceId]);

  const saveProfile = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/account/profile", {
        first_name: profile.first_name,
        last_name: profile.last_name,
        phone: profile.phone,
        directory_profile_update_mode: profile.update_mode,
      });
      setUser(data.user);
      setProposalCandidates(data.directory_proposal_candidates || 0);
      toast.success("Informations mises à jour.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const proposeAll = async () => {
    setProposingAll(true);
    try {
      await api.post("/account/directories/propose-updates");
      setProposalCandidates(0);
      toast.success("Vos modifications ont été proposées aux annuaires concernés.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setProposingAll(false); }
  };

  const changePassword = async () => {
    if (pwd.new_password.length < 8) return toast.error("Le nouveau mot de passe est trop court.");
    if (pwd.new_password !== pwd.confirm) return toast.error("La confirmation ne correspond pas.");
    setChangingPwd(true);
    try {
      await api.post("/account/change-password", { current_password: pwd.current_password, new_password: pwd.new_password });
      setPwd({ current_password: "", new_password: "", confirm: "" });
      toast.success("Mot de passe modifié.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setChangingPwd(false); }
  };

  return (
    <div data-testid="profile-page" className="space-y-8">
      <div>
        <div className="flex items-center gap-2 text-emerald-600 mb-1"><User className="w-5 h-5" /><span className="text-xs font-semibold uppercase tracking-[0.16em]">Compte</span></div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight">Mon profil</h1>
        <p className="text-slate-500 mt-2">Vos informations personnelles restent indépendantes du Workspace actif.</p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <section className="glass rounded-2xl p-6">
          <h2 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2"><User className="w-5 h-5 text-emerald-600" /> Informations personnelles</h2>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="mb-1.5 block">Prénom</Label><Input value={profile.first_name} onChange={(e) => setProfile((p) => ({ ...p, first_name: e.target.value }))} /></div>
              <div><Label className="mb-1.5 block">Nom</Label><Input value={profile.last_name} onChange={(e) => setProfile((p) => ({ ...p, last_name: e.target.value }))} /></div>
            </div>
            <div><Label className="mb-1.5 block">Téléphone</Label><Input value={profile.phone} onChange={(e) => setProfile((p) => ({ ...p, phone: e.target.value }))} placeholder="+687 ..." /></div>
            <div>
              <Label className="mb-1.5 block">E-mail de connexion</Label>
              <div className="flex gap-2"><Input value={user?.email || ""} readOnly disabled className="flex-1" /><Button variant="outline" onClick={() => setEmailOpen(true)}>Modifier</Button></div>
            </div>
            <div><Label className="mb-1.5 block">Société du Workspace actif</Label><Input value={activeWorkspace?.company_name || activeWorkspace?.name || "—"} readOnly disabled /></div>
            <div><Label className="mb-1.5 block">Pays du Workspace actif</Label><Input value={activeWorkspace?.country_code ? countryName(activeWorkspace.country_code, i18n.language) : "—"} readOnly disabled /></div>
            <div>
              <Label className="mb-1.5 block">Propagation de mon profil</Label>
              <Select value={profile.update_mode} onValueChange={(v) => setProfile((p) => ({ ...p, update_mode: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucune</SelectItem>
                  <SelectItem value="ask">Me demander avant de proposer</SelectItem>
                  <SelectItem value="automatic_proposal">Proposer automatiquement</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={saveProfile} disabled={saving} className="w-full bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />} Enregistrer
            </Button>
            {proposalCandidates > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                <p className="text-xs text-amber-800 mb-2">Vos nouvelles coordonnées peuvent être proposées à {proposalCandidates} organisation(s).</p>
                <Button size="sm" onClick={proposeAll} disabled={proposingAll} className="bg-amber-500 hover:bg-amber-400 text-amber-950 font-semibold">
                  {proposingAll ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Proposer mes modifications
                </Button>
              </div>
            )}
          </div>
        </section>

        <section className="glass rounded-2xl p-6">
          <h2 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2"><KeyRound className="w-5 h-5 text-cyan-600" /> Mot de passe</h2>
          <div className="space-y-4">
            <div><Label className="mb-1.5 block">Mot de passe actuel</Label><Input type="password" value={pwd.current_password} onChange={(e) => setPwd((p) => ({ ...p, current_password: e.target.value }))} /></div>
            <div><Label className="mb-1.5 block">Nouveau mot de passe</Label><Input type="password" value={pwd.new_password} onChange={(e) => setPwd((p) => ({ ...p, new_password: e.target.value }))} /></div>
            <PasswordPolicyChecklist policy={pwdPolicy} value={pwd.new_password} expiry={pwdExpiry} />
            <div><Label className="mb-1.5 block">Confirmer le nouveau mot de passe</Label><Input type="password" value={pwd.confirm} onChange={(e) => setPwd((p) => ({ ...p, confirm: e.target.value }))} /></div>
            <Button onClick={changePassword} disabled={changingPwd || !pwd.current_password || !pwd.new_password} className="w-full bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">
              {changingPwd ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />} Changer le mot de passe
            </Button>
          </div>
        </section>
      </div>

      <UserDirectories />
      <EmailChangeDialog open={emailOpen} onOpenChange={setEmailOpen} currentEmail={user?.email || ""} onDone={(u, candidates) => { setUser(u); setProposalCandidates(candidates); }} />
    </div>
  );
};
